import type { LstRegistryEntry } from '../registry'
import { readerBeetsStS } from './beets'
import { readerBenqiSavax } from './benqi'
import { readerBgtWrapper1to1 } from './bgtWrapper'
import { readerDineroBeraEth } from './dinero'
import { readerErc4626, readerErc4626PreviewRedeem } from './erc4626'
import { readerEtherFiWeEth } from './etherfi'
import { readerFlarePooledFlr } from './flarePooled'
import { readerHyperbeatBeHype } from './hyperbeat'
import { readerKelpRsEth } from './kelp'
import { readerKinetiqKHype } from './kinetiq'
import { readerLairStKaia } from './lair'
import { readerLidoWstEth } from './lido'
import { readerListaSlisBnb } from './lista'
import { readerMantleMEth } from './mantle'
import { readerOffChain } from './offChain'
import { readerRenzoEzEth } from './renzo'
import { readerRocketReth } from './rocketpool'
import { readerStaderEthx, readerStaderMaticX } from './stader'
import { readerStakeWiseOsEth } from './stakewise'
import { readerStCelo } from './stcelo'
import { readerSwellGetRate } from './swell'
import { readerValantisWstHype } from './valantis'
import { readerVedaAccountant } from './veda'
import { readerAnkrRatio } from './ankr'
import { readerBinanceWbeth } from './binance'
import { readerCoreEarnRate, readerCoreStakedRatio } from './core'
import { readerKintsuSMon } from './kintsu'
import { readerTreehouseTAsset } from './treehouse'
import type { LstReader } from './shared'

export type { LstCall, LstOnChainState, LstReader } from './shared'
export { ONE_E18, toBigInt } from './shared'

/**
 * Dispatch a registry entry to its per-protocol reader.
 *
 * Single source of truth for the reader-kind → implementation
 * mapping. Adding a new LST means:
 *   1. Add an ABI fragment under `../abis/<protocol>.ts`.
 *   2. Add a reader under `./readers/<protocol>.ts`.
 *   3. Extend `LstReaderKind` in `../registry.ts` and add a case
 *      below.
 *   4. Register the LST in `LST_REGISTRY` with the new reader kind.
 *
 * Each reader contributes 1–3 multicall items + a parser that consumes
 * exactly `calls.length` results. The orchestrator
 * (`../fetchPublic.ts`) flattens every entry's contribution into a
 * single multicall and slices the result back per-entry.
 */
export const buildReader = (entry: LstRegistryEntry): LstReader => {
  switch (entry.reader) {
    case 'erc4626':
      return readerErc4626(entry)
    case 'erc4626PreviewRedeem':
      return readerErc4626PreviewRedeem(entry)
    case 'lidoWstEth':
      return readerLidoWstEth(entry)
    case 'flarePooledFlr':
      return readerFlarePooledFlr(entry)
    case 'listaSlisBnb':
      return readerListaSlisBnb(entry)
    case 'rocketReth':
      return readerRocketReth(entry)
    case 'etherfiWeEth':
      return readerEtherFiWeEth(entry)
    case 'renzoEzEth':
      return readerRenzoEzEth(entry)
    case 'kelpRsEth':
      return readerKelpRsEth(entry)
    case 'swellGetRate':
      return readerSwellGetRate(entry)
    case 'binanceWbeth':
      return readerBinanceWbeth(entry)
    case 'stakewiseOsEth':
      return readerStakeWiseOsEth(entry)
    case 'staderEthx':
      return readerStaderEthx(entry)
    case 'staderMaticX':
      return readerStaderMaticX(entry)
    case 'mantleMEth':
      return readerMantleMEth(entry)
    case 'beetsStS':
      return readerBeetsStS(entry)
    case 'benqiSavax':
      return readerBenqiSavax(entry)
    case 'kinetiqKHype':
      return readerKinetiqKHype(entry)
    case 'lairStKaia':
      return readerLairStKaia(entry)
    case 'valantisWstHype':
      return readerValantisWstHype(entry)
    case 'vedaAccountant':
      return readerVedaAccountant(entry)
    case 'stceloManager':
      return readerStCelo(entry)
    case 'hyperbeatBeHype':
      return readerHyperbeatBeHype(entry)
    case 'dineroBeraEth':
      return readerDineroBeraEth(entry)
    case 'bgtWrapper1to1':
      return readerBgtWrapper1to1(entry)
    case 'offChain':
      return readerOffChain(entry)
    case 'ankrRatio':
      return readerAnkrRatio(entry)
    case 'coreEarnRate':
      return readerCoreEarnRate(entry)
    case 'coreStakedRatio':
      return readerCoreStakedRatio(entry)
    case 'kintsuSMon':
      return readerKintsuSMon(entry)
    case 'treehouseTAsset':
      return readerTreehouseTAsset(entry)
  }
}
